import React, { useEffect, useState, useCallback } from 'react';
import { Settings, Plus, UserCheck, UserX } from 'lucide-react';
import api from '../../api';
import { User } from '../../types';
import Modal from '../../components/Modal';

const UserManagementPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', password: '', role: 'sales' as 'admin' | 'sales',
  });
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/users');
      setUsers(res.data.data || res.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async () => {
    setFormError('');
    if (!form.email.trim() || !form.firstName.trim() || !form.lastName.trim() || !form.password.trim()) {
      setFormError('Tous les champs sont obligatoires.');
      return;
    }
    if (form.password.length < 6) {
      setFormError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }
    setFormLoading(true);
    try {
      await api.post('/users', form);
      setShowCreateModal(false);
      setForm({ email: '', firstName: '', lastName: '', password: '', role: 'sales' });
      fetchUsers();
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Erreur lors de la création.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    try {
      await api.patch(`/users/${user.id}`, { isActive: !user.isActive });
      fetchUsers();
    } catch (err) {
      // ignore
    }
  };

  const handleChangeRole = async (user: User, newRole: 'admin' | 'sales') => {
    try {
      await api.patch(`/users/${user.id}`, { role: newRole });
      fetchUsers();
    } catch (err) {
      // ignore
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>
          <span className="inline-flex"><Settings size={28} /> Gestion des utilisateurs</span>
        </h1>
        <div className="page-header-actions">
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={16} /> Créer un utilisateur
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {loading ? (
            <div className="spinner-container"><div className="spinner" /></div>
          ) : error ? (
            <div className="empty-state"><p className="empty-state-title">{error}</p></div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <Settings size={48} />
              <p className="empty-state-title">Aucun utilisateur</p>
            </div>
          ) : (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Rôle</th>
                    <th>Statut</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="font-semibold">{user.firstName} {user.lastName}</td>
                      <td>{user.email}</td>
                      <td>
                        <select
                          className="form-select"
                          value={user.role}
                          onChange={(e) => handleChangeRole(user, e.target.value as 'admin' | 'sales')}
                          style={{ width: 'auto', padding: '4px 30px 4px 8px', fontSize: '0.85rem' }}
                        >
                          <option value="admin">Admin</option>
                          <option value="sales">Commercial</option>
                        </select>
                      </td>
                      <td>
                        <span className={`badge ${user.isActive !== false ? 'badge-success' : 'badge-danger'}`}>
                          {user.isActive !== false ? 'Actif' : 'Inactif'}
                        </span>
                      </td>
                      <td>
                        <button
                          className={`btn btn-sm ${user.isActive !== false ? 'btn-danger' : 'btn-success'}`}
                          onClick={() => handleToggleActive(user)}
                        >
                          {user.isActive !== false ? (
                            <><UserX size={14} /> Désactiver</>
                          ) : (
                            <><UserCheck size={14} /> Activer</>
                          )}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Créer un utilisateur"
        footer={
          <>
            <button className="btn" onClick={() => setShowCreateModal(false)}>Annuler</button>
            <button className="btn btn-primary" onClick={handleCreate} disabled={formLoading}>
              {formLoading ? <span className="spinner spinner-sm" /> : 'Créer'}
            </button>
          </>
        }
      >
        {formError && <div className="login-error">{formError}</div>}
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Prénom *</label>
            <input className="form-input" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="form-input" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Email *</label>
          <input type="email" className="form-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </div>
        <div className="form-group">
          <label className="form-label">Mot de passe *</label>
          <input type="password" className="form-input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 caractères" />
        </div>
        <div className="form-group">
          <label className="form-label">Rôle</label>
          <select className="form-select" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value as 'admin' | 'sales' })}>
            <option value="sales">Commercial</option>
            <option value="admin">Administrateur</option>
          </select>
        </div>
      </Modal>
    </div>
  );
};

export default UserManagementPage;
